export interface InvoiceListNeighbor {
    id: string;
    page: number;
    /** Vendor, type, date, invoice number, and total. Empty when the row has none of those. */
    tooltipDetail: string;
}

export interface InvoiceNeighborSource {
    _id: string;
    supplierText?: string;
    invoiceType?: string;
    timestamp?: Date | string | null;
    invoiceNumber?: string;
    orderTotal?: number | null;
}

export interface InvoiceDetailNeighborResolution {
    /** The open invoice is one of the rows on this list page. */
    onPage: boolean;
    previous: InvoiceListNeighbor | null;
    next: InvoiceListNeighbor | null;
    /** First row of a page after page 1, and the previous page has not arrived. */
    awaitingPreviousPage: boolean;
    /** Last visible row, another page exists, and that page has not arrived. */
    awaitingNextPage: boolean;
}

function invoiceTypeLabel(invoiceType: string | undefined): string {
    if (invoiceType === 'received') return 'Received';
    if (invoiceType === 'returned') return 'Credited';
    if (invoiceType === 'return_slip') return 'Return Slip';
    if (invoiceType === 'sublet') return 'Sublet';
    return '';
}

function formatNeighborDate(timestamp: Date | string | null | undefined): string {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatNeighborTotal(orderTotal: number | null | undefined): string {
    if (typeof orderTotal !== 'number' || !Number.isFinite(orderTotal)) return '';
    return orderTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Identifying bits shown after "Next invoice:" / "Previous invoice:". */
export function formatInvoiceNeighborDetail(invoice: InvoiceNeighborSource): string {
    const invoiceNumber = invoice.invoiceNumber?.trim() ?? '';
    return [
        invoice.supplierText?.trim() ?? '',
        invoiceTypeLabel(invoice.invoiceType),
        formatNeighborDate(invoice.timestamp),
        invoiceNumber ? `#${invoiceNumber}` : '',
        formatNeighborTotal(invoice.orderTotal),
    ].filter((part) => part.length > 0).join(', ');
}

export function invoiceNeighborTooltip(
    direction: 'Previous' | 'Next',
    neighbor: InvoiceListNeighbor | null,
): string {
    const title = `${direction} invoice`;
    const detail = neighbor?.tooltipDetail.trim() ?? '';
    return detail ? `${title}: ${detail}` : title;
}

function neighborFrom(invoice: InvoiceNeighborSource, page: number): InvoiceListNeighbor {
    return {
        id: invoice._id,
        page,
        tooltipDetail: formatInvoiceNeighborDetail(invoice),
    };
}

export interface FrozenInvoiceNeighborPages {
    pages: Readonly<Record<number, readonly InvoiceNeighborSource[]>>;
    hasNext: Readonly<Record<number, boolean>>;
}

/**
 * Neighbors inside a snapshot of the filtered list. Pages missing from the
 * snapshot are still loading; pages that are present stay even if a later
 * feed refresh drops the open invoice.
 */
export function neighborsFromFrozenPages(
    invoiceId: string,
    frozen: FrozenInvoiceNeighborPages,
): InvoiceDetailNeighborResolution & { found: boolean; page: number | null } {
    const pageNumbers = Object.keys(frozen.pages)
        .map((page) => Number(page))
        .filter((page) => Number.isFinite(page))
        .sort((a, b) => a - b);

    for (const page of pageNumbers) {
        const rows = frozen.pages[page] ?? [];
        if (!rows.some((invoice) => invoice._id === invoiceId)) continue;
        const resolved = resolveInvoiceDetailNeighbors({
            invoiceId,
            page,
            currentPageInvoices: rows,
            hasNextPage: frozen.hasNext[page] === true,
            previousPageInvoices: frozen.pages[page - 1],
            nextPageInvoices: frozen.pages[page + 1],
        });
        return { found: true, page, ...resolved };
    }

    return {
        found: false,
        page: null,
        onPage: false,
        previous: null,
        next: null,
        awaitingPreviousPage: false,
        awaitingNextPage: false,
    };
}

/**
 * Previous and next invoices within the filtered list page the user opened,
 * stepping onto the adjacent page only at the ends of the visible rows.
 */
export function resolveInvoiceDetailNeighbors(args: {
    invoiceId: string;
    page: number;
    currentPageInvoices: ReadonlyArray<InvoiceNeighborSource>;
    hasNextPage: boolean;
    previousPageInvoices?: ReadonlyArray<InvoiceNeighborSource>;
    nextPageInvoices?: ReadonlyArray<InvoiceNeighborSource>;
}): InvoiceDetailNeighborResolution {
    const index = args.currentPageInvoices.findIndex((invoice) => invoice._id === args.invoiceId);
    if (index < 0) {
        return {
            onPage: false,
            previous: null,
            next: null,
            awaitingPreviousPage: false,
            awaitingNextPage: false,
        };
    }

    const samePage = (offset: number): InvoiceListNeighbor => (
        neighborFrom(args.currentPageInvoices[index + offset], args.page)
    );

    let previous: InvoiceListNeighbor | null = index > 0 ? samePage(-1) : null;
    let next: InvoiceListNeighbor | null =
        index < args.currentPageInvoices.length - 1 ? samePage(1) : null;

    const atPageStart = index === 0 && args.page > 1;
    const atPageEnd = next == null && args.hasNextPage;

    if (previous == null && atPageStart && args.previousPageInvoices) {
        const last = args.previousPageInvoices[args.previousPageInvoices.length - 1];
        if (last) previous = neighborFrom(last, args.page - 1);
    }
    if (next == null && atPageEnd && args.nextPageInvoices) {
        const first = args.nextPageInvoices[0];
        if (first) next = neighborFrom(first, args.page + 1);
    }

    return {
        onPage: true,
        previous,
        next,
        awaitingPreviousPage: atPageStart && previous == null && args.previousPageInvoices == null,
        awaitingNextPage: atPageEnd && next == null && args.nextPageInvoices == null,
    };
}
